import React, { useCallback, useMemo } from "react";
import { useFallbackUrl, useNavigator } from "../../components/AppHistory";
import { CustomDialog, Field } from "../../components/CustomDialog";
import { useParams } from "react-router-dom";
import {
  Box,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
} from "@material-ui/core";
import { FinancialIcon, CloseIcon } from "../../icons";
import { ModalComponent } from "../../pages/routes";
import { useAccountPayment } from "./hooks/useAccountPayment";
import { formatMoneyWithSign } from "../../utils/formatMoney";
import { DialogLoader } from "../../components/Loader";
import { formatDate } from "../../utils/formatDate";
import { formatSpacedString } from "../../utils/formatStringSpacing";
import { ColorChip } from "../../components/Chip";
import { APIError } from "../../components/APIError";
import { TextBox } from "../../components/TextBox";

export const PaymentDetails: ModalComponent = ({ fallbackUrl }) => {
  useFallbackUrl(fallbackUrl);
  const { closeOverlay } = useNavigator();
  const { accountNumber, paymentId } = useParams();

  const { data: payment, error, isLoading } = useAccountPayment(
    accountNumber,
    paymentId,
  );

  const onCancel = useCallback(() => {
    closeOverlay();
  }, [closeOverlay]);

  const {
    currency,
    amount,
    method,
    status,
    transactionStatus,
    description,
    reference,
    resolvedDttm,
    createdDttm,
  } = payment || {};
  const {
    chip,
    totalAmount,
    dateLabel,
    dateValue,
    isStruckThrough,
    isGrey,
  } = useMemo(
    () => ({
      isGrey: transactionStatus !== "Accepted",
      isStruckThrough: transactionStatus === "Rejected",
      dateLabel:
        transactionStatus === "Rejected"
          ? "Rejected"
          : transactionStatus === "Accepted"
          ? "Accepted"
          : "Created",
      dateValue:
        transactionStatus === "Rejected" || transactionStatus === "Accepted"
          ? formatDate(resolvedDttm)
          : formatDate(createdDttm),
      chip:
        status !== "Successful" && status != null
          ? formatSpacedString(status)
          : null,
      totalAmount:
        amount === undefined
          ? undefined
          : formatMoneyWithSign(currency, amount),
    }),
    [transactionStatus, resolvedDttm, createdDttm, status, amount, currency],
  );

  return (
    <CustomDialog onClose={onCancel}>
      <DialogTitle data-testid="PaymentDetails__Title">
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="no-wrap"
        >
          <TextBox isGrey={isGrey}>
            <Box display="flex" alignItems="center">
              <Box mr={0.5} lineHeight={0}>
                <FinancialIcon height={24} width={24} />
              </Box>
              <TextBox isStruckThrough={isStruckThrough} isGrey={isGrey}>
                <Typography variant="h6">Payment</Typography>
              </TextBox>
            </Box>
            <Typography variant="body1">{method}</Typography>
          </TextBox>
          <Box mr={-1.5}>
            <IconButton
              onClick={onCancel}
              data-testid="PaymentDetails__Button--close"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers data-testid="PaymentDetails__Content">
        {error ? (
          <APIError
            title="We can't load details for this payment at the moment."
            mb={40}
          />
        ) : isLoading ? (
          <DialogLoader mb={37} />
        ) : (
          payment && (
            <>
              {chip ? (
                <Box
                  display="flex"
                  flexDirection="row"
                  flexWrap="nowrap"
                  alignItems="center"
                  mb={3}
                  data-testid="Detail__Status--chip"
                >
                  <ColorChip label={chip} />
                </Box>
              ) : null}
              <Grid container justify="space-between">
                <Field
                  label="Status"
                  value={transactionStatus}
                  data-testid="Detail__Status--value"
                />
                <Field
                  label={dateLabel}
                  value={dateValue}
                  data-testid="Detail__Resolved--value"
                />
                <Field
                  label="Reference"
                  value={reference}
                  data-testid="Detail__Reference--value"
                />
                <Field
                  label="Description"
                  value={description}
                  data-testid="Detail__Description--value"
                />
                <Field
                  label="Amount"
                  value={totalAmount}
                  data-testid="Detail__Amount--value"
                />
              </Grid>
            </>
          )
        )}
      </DialogContent>
    </CustomDialog>
  );
};
